import * as THREE from 'three';
import { loadAvatar, type AvatarInstance } from '../avatar/VrmAvatar';
import type { PoseMessage } from '../net/NetClient';

const POSITION_LERP = 10; // 越大越跟手，越小越平滑
const YAW_LERP = 8;

/**
 * 远端玩家：和本地玩家**用同一个模型**，但保留头（你看得到对面）。
 *
 * 插值策略：目标是 20 Hz 的姿态包，用帧率无关的指数逼近去追。
 * 不做预测也不做回放——2 人聊天场景里，150 ms 的平滑比"精确"更值钱。
 */
export class RemotePlayer {
  readonly root = new THREE.Group();
  readonly id: string;

  private avatar: AvatarInstance | null = null;
  private readonly current = { x: 0, z: 0, yaw: 0 };
  private readonly target = { x: 0, z: 0, yaw: 0, seated: true };
  private hasTarget = false;
  private mouth = 0;

  constructor(id: string) {
    this.id = id;
    this.root.visible = false;
  }

  async load(url: string, targetHeight = 1.62): Promise<void> {
    const avatar = await loadAvatar(url, { targetHeight });
    this.root.add(avatar.root);
    this.avatar = avatar;
  }

  setPose(pose: PoseMessage): void {
    if (!this.hasTarget) {
      // 第一次收到就直接落位，不要从原点滑过来
      this.current.x = pose.x;
      this.current.z = pose.z;
      this.current.yaw = pose.yaw;
      this.root.position.set(pose.x, 0, pose.z);
      this.root.rotation.y = pose.yaw;
      this.hasTarget = true;
    }
    this.target.x = pose.x;
    this.target.z = pose.z;
    this.target.yaw = pose.yaw;
    this.target.seated = pose.seated;
    this.root.visible = true;
  }

  /** 对方触发的表情（眨眼是各自本地随机的，不需要同步） */
  playExpression(name: string): void {
    this.avatar?.pulseExpression(name);
  }

  /** 对方触发的手势（挥手） */
  playGesture(name: string): void {
    this.avatar?.pulseGesture(name);
  }

  /** 对方的开口度（语音口型，10 Hz） */
  setMouth(value: number): void {
    this.mouth = Math.max(0, Math.min(1, value));
  }

  /** 对方右手的位置（击掌判定用）；模型还没加载好时返回 null */
  handPosition(out?: THREE.Vector3): THREE.Vector3 | null {
    return this.avatar?.handPosition(out) ?? null;
  }

  update(dt: number): void {
    if (!this.hasTarget) return;

    const prevX = this.current.x;
    const prevZ = this.current.z;

    const kp = 1 - Math.exp(-POSITION_LERP * dt);
    this.current.x += (this.target.x - this.current.x) * kp;
    this.current.z += (this.target.z - this.current.z) * kp;

    // 走最短弧
    let dyaw = this.target.yaw - this.current.yaw;
    while (dyaw > Math.PI) dyaw -= Math.PI * 2;
    while (dyaw < -Math.PI) dyaw += Math.PI * 2;
    this.current.yaw += dyaw * (1 - Math.exp(-YAW_LERP * dt));

    this.root.position.set(this.current.x, 0, this.current.z);
    this.root.rotation.y = this.current.yaw;

    // 用插值产生的位移反推速度，驱动程序化步态（不需要网络上传速度）
    const moved = Math.hypot(this.current.x - prevX, this.current.z - prevZ);
    const speed = dt > 1e-4 ? moved / dt : 0;

    this.avatar?.setPose(this.target.seated);
    this.avatar?.setLocomotion(speed);
    // 口型：10 Hz 包太粗，用指数平滑把嘴"化开"，否则像卡帧的机械木偶
    if (this.avatar) {
      const target = this.mouth;
      const smoothed = target > 0.02 ? target : 0;
      this.avatar.setExpression('aa', smoothed);
    }
    this.avatar?.update(dt);
  }

  dispose(): void {
    this.avatar?.dispose();
    this.root.removeFromParent();
  }
}
